/** SEO service landing pages — paths must match router. */
import { PRICING_COPY } from './pricingCopy.js';

export const SERVICE_PAGES = {
  aluminum: {
    path: '/aluminum-patio-covers-vancouver',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    areaServed: 'Vancouver, British Columbia',
    metaTitle: 'Aluminum Patio Covers Vancouver | LoomiHome Patios',
    metaDescription:
      'Aluminum patio covers in Vancouver and the Lower Mainland. Get a patio cover planning range with our Design & Price tool, compare options, then book a free measurement. Strong rain protection.',
    h1: 'Aluminum Patio Covers in Vancouver',
    intro:
      'Durable, low-maintenance aluminum patio covers are a practical choice for rain and sun in the Lower Mainland. LoomiHome helps you see quick ballpark pricing before you commit to a site visit — so you can compare options on your own timeline.',
    benefits: [
      'Strong weather resistance for Vancouver seasons',
      'Low upkeep compared to wood',
      'Clean look that works with most homes',
      'Get a patio cover planning range with our Design & Price tool — no waiting days for a first number',
    ],
    sections: [
      {
        h2: 'Best fit for rainy Lower Mainland patios',
        body: 'Aluminum is usually the first option we compare when homeowners want dependable rain coverage without making the patio feel complicated. It works well for everyday seating areas, side-yard covers, and carport-style spaces where function matters as much as appearance.',
      },
      {
        h2: 'What we confirm during measurement',
        body: 'The free site visit checks width, projection, wall attachment, post placement, gutter direction, and any details that affect final price. That keeps the online estimate useful while still leaving room for accurate structural decisions on site.',
      },
    ],
    pricingLine:
      'Many entry-style aluminum cover projects start in the neighbourhood of our homepage “from” pricing — your online estimate adjusts for size, attachment, and site details.',
    ctaTitle: 'Get a fast rough estimate',
    ctaBody:
      'Use the online Design & Price tool for patio cover planning ranges. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    faqs: [
      {
        q: 'How fast can I get a rough aluminum patio cover quote?',
        a: 'Usually about a minute in our Design & Price It Yourself tool once you enter your city and approximate patio size — then we refine on a free visit if you want to proceed.',
      },
      {
        q: 'How much does an aluminum patio cover cost in Vancouver?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Vancouver patio cover cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Is aluminum better than glass for Vancouver rain?',
        a: 'Aluminum roofs are the straightforward rain shield; glass adds light with proper engineering. We help you compare both for your goals.',
      },
      {
        q: 'Can I get pricing before a measurement?',
        a: 'Yes — ballpark first, measured final price second. That is how we run things.',
      },
      {
        q: 'Do you serve cities outside Vancouver?',
        a: 'Yes — Burnaby, Richmond, Surrey, Delta, Coquitlam, and nearby. See our city pages for local notes.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Lower Mainland aluminum patio cover — planning reference for Vancouver backyards',
      caption:
        'Lower Mainland aluminum patio cover example used for Vancouver planning — practical rain protection. Compare glass and skyline combo in the online Design & Price tool using the same size.',
      projectPath: '/projects/vancouver-aluminum-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/projects/vancouver-aluminum-patio-cover', label: 'Vancouver aluminum patio cover project' },
      { path: '/aluminum-patio-covers-burnaby', label: 'Aluminum patio covers in Burnaby' },
    ],
  },
  glass: {
    path: '/glass-patio-covers-vancouver',
    heroImage: '/house/glass/glass-hero.png',
    areaServed: 'Vancouver, British Columbia',
    metaTitle: 'Glass Patio Covers Vancouver — See Your Price in 60 Seconds | LoomiHome Patios',
    metaDescription:
      'Glass patio covers in Vancouver — bright, modern, rain-ready. Design yours online and see your price in 60 seconds, then book a free on-site measurement.',
    h1: 'Glass Patio Covers in Vancouver',
    intro:
      'Glass patio covers add brightness and a premium feel while keeping the weather out. We serve Vancouver-area homeowners who want clarity first: a fast rough estimate, then a free measurement when you want to move forward.',
    benefits: [
      'Tempered glass for durability',
      'More daylight on your patio',
      'Modern, open look',
      'Compare against aluminum and combo covers in one flow',
    ],
    sections: [
      {
        h2: 'When glass is worth comparing',
        body: 'Glass patio covers are strongest when the homeowner wants weather protection but does not want to darken the back of the house. They are popular for kitchens, dining rooms, and living rooms that face the patio because natural light stays part of the space.',
      },
      {
        h2: 'Rain performance still depends on the details',
        body: 'A glass roof still needs proper slope, flashing, guttering, and attachment. We talk through the budget range online first, then confirm the exact layout and drainage details during the measurement visit.',
      },
    ],
    pricingLine:
      'Glass systems typically sit above basic aluminum on budget — our Design & Price tool gives you a realistic ballpark for your footprint before we measure.',
    ctaTitle: 'See a quick ballpark price',
    ctaBody:
      'Start with an instant-style estimate so you understand range and options. There is no pressure to book until you are ready — free on-site measurement comes after.',
    faqs: [
      {
        q: 'Glass vs aluminum — which should I estimate first?',
        a: 'If budget and rain protection drive the decision, start with aluminum, then compare glass. If light and look are priorities, flip the order. The online Design & Price tool compares both using the same dimensions.',
      },
      {
        q: 'How fast is the patio cover estimate?',
        a: 'Roughly 60 seconds in our Design & Price It Yourself tool. Your formal quote is confirmed after free on-site measurement.',
      },
      {
        q: 'How much does a glass patio cover cost in Vancouver?',
        a: `${PRICING_COPY.glassOnly} Final price depends on span, posts, attachment, and drainage confirmed on site.`,
      },
      {
        q: 'Do glass covers work in heavy rain?',
        a: 'When engineered and installed correctly, yes — slope, drainage, and attachment all matter. We confirm details on site.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Vancouver glass patio cover after installation with tempered glass panels and black aluminum frame',
      caption:
        'Vancouver glass patio cover — keeps the patio and back of the home bright while adding rain protection. See the price for your size in our Design & Price tool.',
      projectPath: '/projects/vancouver-glass-patio-cover',
    },
    relatedPageLinks: [
      { path: '/patio-cover-contractor-vancouver', label: 'Patio cover contractor in Vancouver' },
      { path: '/glass-patio-covers-richmond', label: 'Glass patio covers in Richmond' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
    ],
  },
  skyline: {
    path: '/skyline-combo-patio-covers-vancouver',
    heroImage: '/house/skyline/skyline-hero.png',
    areaServed: 'Vancouver, British Columbia',
    metaTitle: 'Skyline Combo Covers Vancouver | LoomiHome Patios',
    metaDescription:
      'Skyline combo patio covers (glass + V-panel) in Vancouver. Quick estimate, compare with other types, free on-site measurement across the Lower Mainland.',
    h1: 'Skyline Combo Patio Covers in Vancouver',
    intro:
      'Skyline combo designs blend glass and V-panels for balanced light and shade. If you are researching patio covers in Vancouver, we make the first step fast: rough pricing and easy comparison before you book an on-site visit.',
    benefits: [
      'Balanced light and shade',
      'Distinct, architectural look',
      'Good fit when you want more than a flat solid cover',
      'Same quick estimate flow as our other cover types',
    ],
    sections: [
      {
        h2: 'A middle option between solid and glass',
        body: 'Skyline combo covers are useful when a fully solid roof feels too dark but a full glass roof feels too bright or too premium. Mixing glass with V-panel sections lets you tune the patio for light, shade, and budget.',
      },
      {
        h2: 'Good for design-focused backyards',
        body: 'This style is often chosen for newer homes, feature patios, and spaces where the roof is visible from inside the house. We can compare combo, glass, and aluminum in one estimate so you see the trade-offs clearly.',
      },
    ],
    pricingLine:
      'Combo systems often fall between premium glass and value aluminum depending on glass share and span — use our Design & Price tool to see the price for your size.',
    ctaTitle: 'Design & price your cover',
    ctaBody:
      'Use the Design & Price It Yourself tool for a ballpark in minutes. When you want exact measurements and final numbers, we book a free site visit — no obligation.',
    faqs: [
      {
        q: 'What is a skyline combo cover?',
        a: 'A mix of glass and V-panel sections for both light and shade — good when you do not want an all-glass or all-solid roof.',
      },
      {
        q: 'Can I compare combo vs aluminum in one go?',
        a: 'Yes — that is what the online estimate flow is for.',
      },
      {
        q: 'Do I need a permit?',
        a: 'Depends on your city and design. See our permit guide for general guidance, and confirm with your municipality.',
      },
      {
        q: 'How much does a skyline combo patio cover cost in Vancouver?',
        a: 'Combo covers usually land between a solid aluminum roof and a full glass roof on the same footprint, depending on how much glass you choose. Our Design & Price tool gives a planning total from rough dimensions; the Vancouver cost guide explains what moves it, and free measurement confirms the formal quote.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover with glass and V-panel sections on a Metro Vancouver backyard',
      caption:
        'Skyline combo cover — balanced light and shade for Vancouver-area patios. Compare aluminum and full glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
    relatedPageLinks: [
      { path: '/skyline-combo-patio-covers-surrey', label: 'Skyline combo covers in Surrey' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost guide' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
    ],
  },
  sunrooms: {
    path: '/sunrooms-vancouver',
    heroImage: '/house/sunrooms/sunroom-hero.png',
    areaServed: 'Vancouver, British Columbia',
    metaTitle: 'Sunrooms Vancouver | LoomiHome Patios',
    metaDescription:
      'Sunrooms in Vancouver & Lower Mainland. Enclosed glass living space, design consultation, and free on-site measurement.',
    h1: 'Sunrooms in Vancouver',
    intro:
      'Sunrooms extend usable living space with insulated, glass-forward builds. Since each layout and buildable area is different, we confirm the design and pricing during a free on-site measurement.',
    benefits: [
      'Year-round comfort focus',
      'Adds usable square footage feel',
      'Thermal-break aluminum framing options',
      'Estimate first, measurement second — your pace',
    ],
    sections: [
      {
        h2: 'More enclosed than a patio cover',
        body: 'A sunroom is a bigger decision than an open patio cover because it adds walls, more glass, and more comfort planning. It can be a strong fit when you want a brighter sitting area, hobby space, or protected transition between indoors and outdoors.',
      },
      {
        h2: 'Start with budget before design details',
        body: 'Sunroom pricing depends on the buildable area, layout, enclosure level, and site conditions. A free on-site measurement lets us review those details before confirming a quote.',
      },
    ],
    pricingLine:
      'Sunrooms usually require a larger investment than open patio covers. We confirm pricing after reviewing buildable area and site conditions during a free on-site measurement.',
    ctaTitle: 'Book a free sunroom measurement',
    ctaBody:
      'Discuss sunroom and patio cover options, then book a free on-site measurement. We confirm sunroom pricing after reviewing the buildable area and site conditions.',
    faqs: [
      {
        q: 'Are sunrooms more expensive than patio covers?',
        a: PRICING_COPY.sunroomMoreExpensive,
      },
      {
        q: 'How long does installation take?',
        a: 'Scope drives the calendar. We give a realistic range after measurement; see our timeline guide for typical phases.',
      },
      {
        q: 'Can I get a sunroom price online?',
        a: PRICING_COPY.sunroomCost,
      },
      {
        q: 'How much does a sunroom cost in Vancouver?',
        a: 'Sunroom pricing depends on the buildable area, enclosure design, and site conditions. We confirm it after a free on-site measurement; the online tool is for open patio cover planning ranges.',
      },
    ],
    caseStudy: {
      image: '/house/realprojects/sunroom-white.png',
      alt: 'White-framed sunroom with glass walls attached to a Vancouver-area home',
      caption:
        'Sunroom enclosure example for Vancouver homeowners comparing a brighter enclosed space against an open patio cover.',
    },
    relatedPageLinks: [
      { path: '/sunrooms-burnaby', label: 'Sunrooms in Burnaby' },
      { path: '/sunrooms-richmond', label: 'Sunrooms in Richmond' },
      { path: '/sunrooms-surrey', label: 'Sunrooms in Surrey' },
      { path: '/sunrooms-coquitlam', label: 'Sunrooms in Coquitlam' },
      { path: '/sunrooms-langley', label: 'Sunrooms in Langley' },
      { path: '/sunrooms-delta', label: 'Sunrooms in Delta' },
      { path: '/patio-cover-contractor-vancouver', label: 'Patio cover contractor in Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  carport: {
    path: '/aluminum-carports-vancouver',
    heroImage: '/house/before-after/richmond-carport-after.png',
    areaServed: 'Metro Vancouver, British Columbia',
    metaTitle: 'Aluminum Carports Vancouver | LoomiHome Patios',
    metaDescription:
      'Aluminum carport covers in Metro Vancouver — freestanding or attached, with solid aluminum, glass, or polycarbonate roof options. Fast ballpark with our Design & Price tool, free on-site measurement, and full-size displays at our Burnaby showroom.',
    h1: 'Aluminum Carports in Metro Vancouver',
    intro:
      'An aluminum carport cover keeps vehicles, boats, and side-driveway storage dry through Metro Vancouver rain — without the cost or footprint of a full garage build. LoomiHome installs freestanding and house-attached carports on the same aluminum system as our patio covers, and you can see a fast ballpark price online before booking a free measurement.',
    benefits: [
      'Freestanding or attached to the house — layouts built around your driveway',
      'Powder-coated aluminum frame that will not rot, peel, or need repainting',
      'Solid aluminum, glass, or polycarbonate roof options',
      'Dry parking and covered side-driveway storage year-round',
      'Fast ballpark with our Design & Price tool — no waiting days for a first number',
    ],
    sections: [
      {
        h2: 'Freestanding or attached — planned around your driveway',
        body: 'Some carports attach to the house along a side driveway; others stand on their own posts over a parking pad. During the free measurement we confirm vehicle clearance, span, post placement so doors and walkways stay clear, gutter direction, and drainage — the details that decide whether a carport feels easy to use every day.',
      },
      {
        h2: 'See cover systems in person at the Burnaby showroom',
        body: 'Our Burnaby showroom has full-size patio cover and sunroom displays, so you can compare aluminum frames and roof panels in person before deciding. If a carport is the right fit, we book a free on-site measurement anywhere in Metro Vancouver to confirm the layout and give you firm pricing.',
      },
    ],
    pricingLine:
      'Carport pricing depends on span, roof style, attachment, and post placement — our Design & Price tool gives you a planning total for your driveway size first, and the free measurement confirms the formal quote.',
    ctaTitle: 'Get a fast rough estimate',
    ctaBody:
      'Use our 60-second Design & Price tool for a ballpark on your carport size, then book a free on-site measurement when the range works for you. Prefer to look first? Visit our Burnaby showroom to see the systems up close.',
    faqs: [
      {
        q: 'Can a carport be freestanding, or does it have to attach to the house?',
        a: 'Both work. Attached carports run along the house — common for side driveways — while freestanding carports stand on their own posts over a driveway or parking pad. We confirm which layout suits your lot during the free measurement.',
      },
      {
        q: 'What roof options are available for an aluminum carport?',
        a: 'Solid aluminum panels for maximum rain protection and shade, glass for a brighter, more open look, or polycarbonate panels as a lighter-weight translucent option. All three run on the same aluminum frame system.',
      },
      {
        q: 'How much does an aluminum carport cost in Metro Vancouver?',
        a: 'It depends on span, roof style, and post placement. Our Design & Price tool gives a planning total for your size in about a minute, and your formal quote is confirmed after a free on-site measurement.',
      },
      {
        q: 'Do I need a permit for a carport cover?',
        a: 'Requirements vary by city and by the size and placement of the structure. We flag the likely requirements during measurement, and you should confirm with your municipality before building.',
      },
      {
        q: 'Can I see a carport before ordering?',
        a: 'You can see full-size cover systems at our Burnaby showroom, and our Richmond carport project page shows a completed side-driveway installation.',
      },
    ],
    caseStudy: {
      image: '/house/before-after/richmond-carport-after.png',
      alt: 'Aluminum carport cover over a Richmond side driveway with aluminum frame and solid roof panels',
      caption:
        'Richmond aluminum carport cover — a side-driveway installation that turned open parking into dry, covered space. See the full project for before-and-after photos.',
      projectPath: '/projects/richmond-aluminum-carport-cover',
    },
    relatedPageLinks: [
      { path: '/instant-quote', label: 'Design & Price It Yourself — instant quote tool' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/projects/richmond-aluminum-carport-cover', label: 'Richmond aluminum carport project' },
      { path: '/patio-cover-showroom-burnaby', label: 'Patio cover showroom in Burnaby' },
    ],
  },
};

export const SERVICE_PAGE_ORDER = ['aluminum', 'glass', 'skyline', 'sunrooms', 'carport'];
